// Layer 3 (Transformation) — the 5 × 5 heat map inside "Where the risk sits"
// (polish.P20, locked comp `risk.html` A). The map goes quiet: each cell
// carries its band as a soft wash, only the risk ids in a cell take state ink,
// and an empty cell shows its L × C score in ink-tertiary. The consequence
// axis follows the lens (adjudication 2); `worst` draws today's positions.
// Pure: no React.

import { RISK_ENTRIES } from './seed';
import {
  consequenceFor,
  riskBandForScore,
  riskRegister,
  riskScore,
  type RiskBand,
  type RiskConsequenceMode,
  type RiskScoreAxis,
} from './Scene.transform';

export type HeatMapCell = {
  key: string;
  likelihood: RiskScoreAxis;
  consequence: RiskScoreAxis;
  score: number;
  band: RiskBand;
  riskIds: readonly string[];
  fill: string;
  border: string;
  /** Ink for the risk ids in the cell: state ink for Critical and High only. */
  ink: string;
};

export type HeatMapLegendStop = {
  band: RiskBand;
  label: string;
  fill: string;
};

export type HeatMapRow = {
  consequence: RiskScoreAxis;
  cells: readonly HeatMapCell[];
};

export type RiskHeatMapView = {
  mode: RiskConsequenceMode;
  cells: readonly HeatMapCell[];
  rows: readonly HeatMapRow[];
  likelihoodTicks: readonly RiskScoreAxis[];
  consequenceTicks: readonly RiskScoreAxis[];
  legend: readonly HeatMapLegendStop[];
};

export const HEAT_MAP_LIKELIHOOD_TICKS: readonly RiskScoreAxis[] = [1, 2, 3, 4, 5];
export const HEAT_MAP_CONSEQUENCE_TICKS: readonly RiskScoreAxis[] = [5, 4, 3, 2, 1];

/** Band → soft wash (comp `.hm .c.b-*`): Critical the blocked wash, High the
 *  warning wash, Medium surface-3, Low surface-1. */
function fillForBand(band: RiskBand): string {
  if (band === 'critical') return 'color-mix(in srgb, var(--state-blocked) 9%, var(--surface-2))';
  if (band === 'high') return 'color-mix(in srgb, var(--state-warning) 11%, var(--surface-2))';
  if (band === 'medium') return 'var(--surface-3)';
  return 'var(--surface-1)';
}

function borderForBand(band: RiskBand): string {
  if (band === 'critical') return 'color-mix(in srgb, var(--state-blocked) 22%, transparent)';
  if (band === 'high') return 'color-mix(in srgb, var(--state-warning) 22%, transparent)';
  return 'var(--hairline)';
}

function inkForBand(band: RiskBand): string {
  if (band === 'critical') return 'var(--state-blocked)';
  if (band === 'high') return 'var(--state-warning)';
  return 'var(--ink-primary)';
}

export const HEAT_MAP_LEGEND: readonly HeatMapLegendStop[] = (
  [
    ['low', 'Low'],
    ['medium', 'Medium'],
    ['high', 'High'],
    ['critical', 'Critical'],
  ] as const
).map(([band, label]) => ({ band, label, fill: fillForBand(band) }));

export function riskHeatMap(mode: RiskConsequenceMode = 'worst'): RiskHeatMapView {
  // Comp A lists a cell's ids in register (budget-impact) order — one rank
  // owner, `riskRegister()`, shared with the register tab.
  const rankById = new Map(riskRegister().map((row) => [row.id, row.rank]));
  const rows: HeatMapRow[] = [];
  const cells: HeatMapCell[] = [];
  for (const consequence of HEAT_MAP_CONSEQUENCE_TICKS) {
    const rowCells: HeatMapCell[] = [];
    for (const likelihood of HEAT_MAP_LIKELIHOOD_TICKS) {
      const score = likelihood * consequence;
      const band = riskBandForScore(score);
      const riskIds = RISK_ENTRIES.filter(
        (risk) => riskScore(risk).likelihood === likelihood && consequenceFor(risk, mode) === consequence,
      )
        .map((risk) => risk.id)
        .sort((left, right) => (rankById.get(left) ?? 0) - (rankById.get(right) ?? 0));
      const cell: HeatMapCell = {
        key: `${likelihood}-${consequence}`,
        likelihood,
        consequence,
        score,
        band,
        riskIds,
        fill: fillForBand(band),
        border: borderForBand(band),
        ink: inkForBand(band),
      };
      rowCells.push(cell);
      cells.push(cell);
    }
    rows.push({ consequence, cells: rowCells });
  }
  return {
    mode,
    cells,
    rows,
    likelihoodTicks: HEAT_MAP_LIKELIHOOD_TICKS,
    consequenceTicks: HEAT_MAP_CONSEQUENCE_TICKS,
    legend: HEAT_MAP_LEGEND,
  };
}
